import moment from 'moment';
import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import SessionsImg from '../../assets/img/sessions.svg';
import { useTranslation } from 'react-i18next';

// Components
import Paginate from 'src/ui/pagination/Paginate';
import Session from 'src/components/cards/therapy/Session';

// Redux
import { setChangePasswordView } from 'src/redux/slices/auth.slice';

// Services
import { getPastSessionsUseCase, getUpcomingSessionsUseCase } from 'src/services/therapy/useCases';
import { dateLocaleToUTC } from 'src/common/helpers/utcToLocale';

const TherapistAppointments = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['therapyAppointment']);
  const [currentPagePastSession, setCurrentPagePastSession] = useState<number>(1);
  const [currentPageUpcomingSession, setCurrentPageUpcomingSession] = useState<number>(1);

  const newDate = moment().format('YYYY-MM-DDTHH:mm:00');

  const {
    upcomingSessions,
    upcomingSessionsMetadata,
    pastSessionMetadata,
    pastSessions,
    summaryUpload,
  } = useAppSelector((state) => state.therapy);

  // Get data from backend
  useEffect(() => {
    const UTCDate = dateLocaleToUTC(newDate);
    dispatch(getPastSessionsUseCase(currentPagePastSession, UTCDate, true));
  }, [currentPagePastSession, dispatch, newDate, summaryUpload]);

  useEffect(() => {
    const UTCDate = dateLocaleToUTC(newDate);
    dispatch(getUpcomingSessionsUseCase(currentPageUpcomingSession, UTCDate, false));
  }, [currentPageUpcomingSession, dispatch, newDate]);

  useEffect(() => {
    dispatch(setChangePasswordView(''));
  }, [dispatch]);

  // Use fallback or backend data based on what's available
  const displayPastSessions = pastSessions;
  const displayUpcomingSessions = upcomingSessions;
  const displayPastMetadata = pastSessionMetadata;
  const displayUpcomingMetadata = upcomingSessionsMetadata;

  return (
    <div
      className='relative flex flex-col min-w-0 break-words w-full shadow-lg rounded-2xl bg-white text-primary'
      style={{ height: '75vh' }}
    >
      <div className='flex items-center p-3 bg-background rounded-t-2xl'>
        <span className='flex font-semibold text-sm mx-2 uppercase'>{t('header')}</span>
      </div>
      <hr />
      <div className='flex h-[90%] gap-5'>
        <div className='w-2/4 flex items-center justify-center ml-5'>
          <div className='sessions-card flex flex-col items-center w-full'>
            <h1 className='text-left text-xl mb-5 w-full px-5'>{t('pastHeader')}</h1>
            {displayPastSessions.length > 0 && (
              <>
                <div className='max-h-full overflow-y-auto w-full pb-4'>
                  {displayPastSessions.map((session: any) => (
                    <div
                      className='mb-4 bg-therapistSessionsBg rounded-lg mx-4'
                      key={session.appointmentId}
                    >
                      <Session session={session} isPastSession />
                    </div>
                  ))}
                </div>
                {displayPastMetadata.pageCount > 1 && (
                  <div className='w-full px-5 pb-4'>
                    <Paginate
                      currentPage={currentPagePastSession}
                      totalPage={displayPastMetadata.pageCount}
                      setCurrentPage={setCurrentPagePastSession}
                    />
                  </div>
                )}
              </>
            )}
            {displayPastSessions.length === 0 && (
              <div className='flex flex-col items-center w-3/5 mt-8'>
                <img src={SessionsImg} alt='No sessions' width={80} />
                <span className='mt-8 text-2xl text-center text-primary font-medium'>
                  {t('noPast')}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className='w-2/4 flex items-center justify-center mr-5'>
          <div className='sessions-card flex flex-col items-center w-full'>
            <h1 className='text-left text-xl mb-5 w-full px-5'>{t('upcomingHeader')}</h1>
            {displayUpcomingSessions.length > 0 && (
              <>
                <div className='max-h-full overflow-y-auto w-full pb-4'>
                  {displayUpcomingSessions.map((session: any) => (
                    <div
                      className='mb-4 bg-therapistSessionsBg rounded-lg mx-4'
                      key={session.appointmentId}
                    >
                      <Session session={session} />
                    </div>
                  ))}
                </div>
                {displayUpcomingMetadata.pageCount > 1 && (
                  <div className='w-full px-5 pb-4'>
                    <Paginate
                      currentPage={currentPageUpcomingSession}
                      totalPage={displayUpcomingMetadata.pageCount}
                      setCurrentPage={setCurrentPageUpcomingSession}
                    />
                  </div>
                )}
              </>
            )}
            {displayUpcomingSessions.length === 0 && (
              <div className='flex flex-col items-center w-3/5 mt-8'>
                <img src={SessionsImg} alt='No sessions' width={80} />
                <span className='mt-8 text-2xl text-center text-primary font-medium'>
                  {t('noUpcoming')}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TherapistAppointments;
